<template>
  <div
    style="width: 100%; height: 100%;"
    :style="`text-align: ${effectiveAlign}`"
  >
    <div v-if="isCheckbox">
      <feather-icon
        v-if="checkboxChecked"
        icon="CheckSquareIcon"
        size="16"
        :style="access === 'rw' ? `cursor: pointer;` : ''"
        @click="editOn"
      />
      <feather-icon
        v-else
        icon="SquareIcon"
        size="16"
        :style="access === 'rw' ? `cursor: pointer;` : ''"
        @click="editOn"
      />
    </div>

    <div v-else-if="!isEdit">
      <div
        v-if="doubleClick"
        class="activator"
        style="min-height: 1rem;"
        @dblclick="editOn"
      >
        {{ record && record[keyValue].value ? (formatted(record[keyValue].value) || '\u00a0') : '\u00a0' }}
      </div>
      <div
        v-else
        class="activator"
        style="min-height: 1rem;"
        @click="editOn"
      >
        {{ record && record[keyValue].value ? (formatted(record[keyValue].value) || '\u00a0') : '\u00a0' }}
      </div>
    </div>

    <div
      v-else-if="valueType === 'date' || valueType === 'datetime'"
      ref="dateWrap"
      class="cell-date-cell"
      @focusout="onDateFocusOut"
    >
      <input
        ref="dateText"
        type="text"
        class="form-control cell-date-text"
        :value="editText"
        placeholder="ДД.ММ.ГГГГ"
        inputmode="numeric"
        maxlength="10"
        autocomplete="off"
        @change="onDateTextChange"
        @keydown="onDateTextKeydown"
      >
      <b-form-datepicker
        v-model="editDate"
        button-only
        right
        size="sm"
        locale="ru"
        class="cell-date-btn"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
        @input="onDatePicked"
        @shown="datePickerOpen = true"
        @hidden="onDatePickerHidden"
      />
    </div>

    <div
      v-else
      :id="'edit_' + uuid"
      class="cell-edit"
      contenteditable="true"
      @blur="onBlur"
      @keydown="onKeyPress"
    />

    <cbs-debug
      v-if="false && isRoot"
      :context="this"
    />
  </div>
</template>

<script>
import { BFormDatepicker } from 'bootstrap-vue'
import CbsDebug from '@/cubus/components/debug/CbsDebug.vue'
import useCubus from '@/cubus/services/useCubus'
import nextCell, { selectAllText } from '@/@core/utils/next-cell'

export default {
  name: 'CbsCellEditable',
  components: {
    CbsDebug,
    BFormDatepicker,
  },
  props: {
    record: {
      type: Object,
      default: null,
    },
    keyValue: {
      type: String,
      default: null,
    },
    valueType: {
      type: String,
      default: 'string',
    },
    access: {
      type: String,
      default: 'ro',
    },
    noNextCell: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'left',
    },
    doubleClick: {
      type: Boolean,
      default: false,
    },
    renderType: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      uuid: useCubus.guid(),
      isEdit: false,
      editDate: '', // yyyy-mm-dd для календаря (b-form-datepicker)
      editText: '', // ДД.ММ.ГГГГ для текстового ввода
      datePickerOpen: false,
    }
  },
  computed: {
    // rendertype=checkbox turns a numeric cell into a done/not-done tick: click writes 1/NULL
    isCheckbox() {
      if (this.valueType === 'bit') return true
      return this.renderType === 'checkbox' && ['number', 'double', 'float', 'int'].includes(this.valueType)
    },
    checkboxChecked() {
      if (!this.record || !this.record[this.keyValue]) return false
      const v = this.record[this.keyValue].value
      if (this.valueType === 'bit') return !!v
      return Number(v) > 0
    },
    effectiveAlign() {
      if (this.isCheckbox) return 'center'
      if (['number', 'double', 'float', 'int', 'percent', 'percent4'].includes(this.valueType)) return 'right'
      return this.align
    },
  },
  created() {
    if (!Object.hasOwn(this.record, this.keyValue)) {
      console.log(this.keyValue, 'does not exist in', this.record)
    }
  },
  setup() {
    return {
      isRoot: useCubus.isRoot(),
    }
  },
  methods: {
    onKeyPress(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.isEdit = false
      }
      nextCell(this.noNextCell, event)
    },
    formatNumber(value) {
      if (!value) return NaN
      if (typeof value !== 'string') return value
      let newValue = value.trim()
      newValue = newValue.replace(/\s/g, '')
      newValue = newValue.replace(',', '.')
      // console.log('newValue', newValue)
      return Number(newValue)
    },
    onBlur(event) {
      if (this.isEdit) {
        this.isEdit = false
        const srcValue = JSON.parse(JSON.stringify(event.target.innerText))
        let newValue = srcValue
        if (this.valueType === 'number' || this.valueType === 'double') {
          newValue = this.formatNumber(srcValue)
        } else if (this.valueType === 'date') {
          newValue = this.parseDate(srcValue)
        } else if (this.valueType === 'datetime') {
          newValue = this.parseDateTime(srcValue)
        } else if (this.valueType.startsWith('percent')) {
          newValue = this.parsePercent(srcValue)
        }
        /*
        if ((this.record && this.record[this.keyValue].value !== newValue) || (!this.record && newValue)) {
          // console.log('newValue', newValue)
          this.$emit('update', newValue)
          return
        }
        */
        // console.log('newValue', newValue)
        this.$emit('update', newValue)
      }
    },
    editOn(selectAll = true) {
      // console.log('editOn', this.record, this.keyValue)
      if (this.record && 'cbs_access' in this.record) {
        if (this.record.cbs_access === 'ro') return
      } else if (this.record && 'cbs_sys_access' in this.record) {
        if (this.record.cbs_sys_access === 'ro') return
      } else if (this.access === 'ro') return
      if (this.valueType === 'bit') {
        // this.record[this.keyValue].value = !this.record[this.keyValue].value
        this.$emit('update', !this.record[this.keyValue].value)
      } else if (this.isCheckbox) {
        // numeric checkbox: done → 1, undone → NULL (no record noise with zeros)
        this.$emit('update', this.checkboxChecked ? null : 1)
      } else if (this.valueType === 'date' || this.valueType === 'datetime') {
        // \u0433\u0438\u0431\u0440\u0438\u0434: \u0442\u0435\u043a\u0441\u0442\u043e\u0432\u044b\u0439 \u0432\u0432\u043e\u0434 \u0414\u0414.\u041c\u041c.\u0413\u0413\u0413\u0413 (\u0441\u0432\u043e\u0439 \u043f\u0430\u0440\u0441\u0435\u0440) + \u0438\u043a\u043e\u043d\u043a\u0430-\u043a\u0430\u043b\u0435\u043d\u0434\u0430\u0440\u044c (\u043d\u0430\u0442\u0438\u0432\u043d\u044b\u0439 \u043f\u0438\u043a\u0435\u0440)
        this.editDate = this.normalizeDate(this.record ? this.record[this.keyValue].value : '')
        this.editText = this.editDate ? this.formatDate(this.editDate) : ''
        this.isEdit = true
        this.$nextTick(() => {
          const el = this.$refs.dateText
          if (!el) return
          el.focus()
          el.select()
        })
      } else {
        this.isEdit = true
        this.$nextTick(() => {
          const el = document.getElementById(`edit_${this.uuid}`)
          el.innerText = this.record ? this.record[this.keyValue].value : '\u00a0'
          el.focus()
          if (selectAll) selectAllText()
        })
      }
    },
    // нормализуем любое входное значение даты к yyyy-mm-dd для пикера
    normalizeDate(v) {
      if (!v || v === '0') return ''
      const s = String(v)
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
      const d = new Date(s)
      if (Number.isNaN(d.getTime())) return ''
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    // детерминированный парсер ручного ввода: ДД.ММ.ГГГГ | ДД/ММ/ГГГГ | ДД ММ ГГГГ | ДДММГГГГ | ГГГГ-ММ-ДД -> yyyy-mm-dd
    toIsoDate(str) {
      const t = String(str).trim()
      // ГГГГ-ММ-ДД (проверяем ДО нормализации разделителей)
      const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
      if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`
      // ДД(. / пробел)ММ(...)ГГГГ
      let m = t.replace(/[/\s.]+/g, '.').match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
      if (!m) {
        const digits = t.replace(/\D/g, '')
        if (digits.length === 8) m = [null, digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)] // ДДММГГГГ
      }
      if (m) {
        const dd = m[1].padStart(2, '0')
        const mm = m[2].padStart(2, '0')
        if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return ''
        return `${m[3]}-${mm}-${dd}`
      }
      return ''
    },
    commitDate(iso) {
      const orig = this.normalizeDate(this.record ? this.record[this.keyValue].value : '')
      if (iso !== orig) this.$emit('update', iso)
    },
    // текстовый ввод подтверждён (Enter/blur/change)
    onDateTextChange(event) {
      const el = event.target
      const raw = el.value
      if (raw.trim() === '') { // очистка
        this.editDate = ''
        this.editText = ''
        this.commitDate('')
        return
      }
      const iso = this.toIsoDate(raw)
      if (iso) {
        this.editDate = iso
        this.editText = this.formatDate(iso)
        el.value = this.editText
        this.commitDate(iso)
      } else {
        // мусор — откатываем поле к текущему значению, дату не трогаем
        el.value = this.editText
      }
    },
    onDateTextKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.isEdit = false
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.onDateTextChange(event)
        this.isEdit = false
      }
    },
    // выбор из календаря b-form-datepicker (@input даёт строку yyyy-mm-dd)
    onDatePicked(iso) {
      const v = iso || ''
      this.editText = v ? this.formatDate(v) : ''
      this.commitDate(v)
    },
    onDatePickerHidden() {
      this.datePickerOpen = false
      // если фокус ушёл за пределы ячейки после закрытия календаря — выходим
      setTimeout(() => {
        if (!this.isEdit || this.datePickerOpen) return
        const el = this.$refs.dateWrap
        if (el && el.contains(document.activeElement)) return
        this.isEdit = false
      }, 0)
    },
    // выход из режима редактирования, когда фокус ушёл за пределы ячейки
    onDateFocusOut() {
      setTimeout(() => {
        if (!this.isEdit || this.datePickerOpen) return // календарь открыт — не выходим
        const el = this.$refs.dateWrap
        if (el && el.contains(document.activeElement)) return // фокус ещё внутри (текст ↔ кнопка)
        this.isEdit = false
      }, 0)
    },
    isDate(dateString) {
      const d = new Date(dateString)
      if (!Number.isNaN(d.getTime())) return true

      const regex = /^(\d{2}).(\d{2}).(\d{4}).*$/
      const regex2 = /^\d{4}-\d{2}-\d{4}.*$/
      if (!dateString.match(regex) && !dateString.match(regex2)) return false
      let dt = dateString
      if (dateString.match(regex)) {
        dt = dateString.replace(regex, '$3-$2-$1')
      }
      const date = new Date(dt)
      const timestamp = date.getTime()
      return !Number.isNaN(timestamp) && dt === date.toISOString().split('T')[0]
    },
    toDate(dateString) {
      if (!this.isDate(dateString)) return null
      // const regex = /^(\d{2}).(\d{2}).(\d{4})$/
      // if (dateString.match(regex)) return dateString.replace(regex, '$3-$2-$1')
      return dateString
    },
    formatted(value) {
      // if (this.valueType === 'date') return this.toDate(value)
      if (this.valueType === 'date' || this.valueType === 'datetime') return this.formatDate(value)
      if (this.valueType === 'number' || this.valueType === 'double') {
        // return Number(value).toLocaleString()
        return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value))
      }
      if (this.valueType === 'percent') return `${(Number(value) * 100).toFixed(2)}%`
      if (this.valueType === 'percent4') return `${(Number(value) * 100).toFixed(4)}%`
      if (value && value.length > 100) return `${value.substring(0, 100)}…`
      return value
    },
    formatDate(dateStr) {
      const dt = new Date(dateStr)
      const yyyy = dt.getFullYear()
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')
      if (!dateStr || dateStr === '' || dateStr === '0') return ''
      // const l = dateStr.split('-')
      // return `${l[2]}.${l[1]}.${l[0]}` // dd.mm.yyyy
      return `${dd}.${mm}.${yyyy}` // dd.mm.yyyy
    },

    formatDateTime_v1(dateStr) {
      console.log('dateStr', dateStr)
      if (!dateStr || dateStr === '' || dateStr === '0') return ''
      const dt = new Date(dateStr)
      if (Number.isNaN(dt.getTime())) return dateStr
      const yyyy = dt.getFullYear()
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')
      const hh = String(dt.getHours()).padStart(2, '0')
      const min = String(dt.getMinutes()).padStart(2, '0')
      return `${dd}.${mm}.${yyyy} ${hh}:${min}`
    },
    formatDateTime(dateStr) {
      if (!dateStr || dateStr === '' || dateStr === '0') return ''

      let dt

      if (/^\d{2}\.\d{2}\.\d{4}/.test(dateStr)) {
        // dd.mm.yyyy или dd.mm.yyyy hh:mm
        const [datePart, timePart] = dateStr.split(' ')
        const [dd, mm, yyyy] = datePart.split('.')
        const [hh = '00', min = '00'] = (timePart || '').split(':')
        dt = new Date(yyyy, mm - 1, dd, hh, min)
      } else {
        // yyyy-mm-dd или yyyy-mm-ddThh:mm:ss
        dt = new Date(dateStr)
      }

      if (!dt || Number.isNaN(dt.getTime())) return dateStr

      const yyyy = dt.getFullYear()
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')
      const hh = String(dt.getHours()).padStart(2, '0')
      const min = String(dt.getMinutes()).padStart(2, '0')
      return `${dd}.${mm}.${yyyy} ${hh}:${min}`
    },
    parseDateTime(str) {
      if (!str || str.trim() === '') return ''
      const trimmed = str.trim()
      // match DD.MM.YYYY HH:mm or DD.MM.YYYY HH:mm:ss
      const fullMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/)
      if (fullMatch) {
        const [, d, m, y, hh = '00', min = '00', ss = '00'] = fullMatch
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')} ${hh.padStart(2, '0')}:${min}:${ss}`
      }
      // fallback: try native Date parse
      const dt = new Date(trimmed)
      if (!Number.isNaN(dt.getTime())) {
        return dt.toISOString().replace('T', ' ').substring(0, 19)
      }
      return trimmed
    },
    parseDate(dateStr) {
      if (!dateStr) return ''
      if (dateStr.match(/^([1-9]|0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/)) {
        return this.parseDdMmYyyy(dateStr, '/', false) || ''
      }
      if (dateStr.match(/^([1-9]|0?[1-9]|[12][0-9]|3[01])\.(0?[1-9]|1[0-2])\.\d{4}$/)) {
        return this.parseDdMmYyyy(dateStr, '.', false) || ''
      }
      if (dateStr.match(/^([1-9]|0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{2}$/)) {
        return this.parseDdMmYyyy(dateStr, '/', true) || ''
      }
      if (dateStr.match(/^([1-9]|0?[1-9]|[12][0-9]|3[01])\.(0?[1-9]|1[0-2])\.\d{2}$/)) {
        return this.parseDdMmYyyy(dateStr, '.', true) || ''
      }
      const l = dateStr.split('.')
      return `${l[2]}-${l[1]}-${l[0]}` // yyyy-mm-dd
    },
    isValidDate(d) {
      return d instanceof Date && !d.isNaN
    },
    parseDdMmYyyy(dateStr, separator, shortYear) {
      const [d, m, y] = dateStr.split(separator).map(Number)
      const dt = new Date(shortYear ? y + 2000 : y, m - 1, d)
      if (this.isValidDate(dt)) {
        return `${shortYear ? y + 2000 : y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      }
      return null
    },

    parsePercent(str) {
      if (str.endsWith('%')) {
        const perc = str.slice(0, -1)
        const v = this.formatNumber(perc)
        return v / 100
      }
      return this.formatNumber(str)
    },
  },
}
</script>

<style scoped>
.activator {
  min-height: 1rem;
  cursor: pointer;
}

.activator:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.cell-edit {
  width: 100%;
  min-height: 1rem;
  padding: 0.25rem 0.5rem;
  outline: none;
  border: none;
  background: transparent;
}

.cell-edit:focus {
  outline: none;
  background-color: rgba(255, 255, 255, 0.9);
}

/* текст во всю ширину ячейки, иконка-календарь поверх правого края */
.cell-date-cell {
  position: relative;
  width: 100%;
  min-width: 6.5rem;
}

.cell-date-cell .cell-date-text {
  width: 100%;
  height: auto;
  padding: 0.2rem 1.6rem 0.2rem 0.4rem; /* справа — место под иконку */
}

.cell-date-cell .cell-date-btn {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
}

/* компактная прозрачная кнопка-иконка внутри b-form-datepicker */
.cell-date-cell .cell-date-btn ::v-deep .btn {
  padding: 0 0.35rem;
  height: 100%;
  border: none;
  background: transparent;
  color: #6e6b7b;
  box-shadow: none;
}

.cell-date-cell .cell-date-btn ::v-deep .btn:hover {
  color: #7367f0;
}
</style>
